import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, AreaChart, Area, CartesianGrid
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28CF6', '#FF6699', '#FF4444', '#00B8D9'];
const ORDER_STATUSES = ['pending', 'processing', 'shipped', 'delivered', 'cancelled'];
const PAYMENT_STATUSES = ['pending', 'paid', 'failed'];

const Analytics = () => {
  const { token } = useAuth();
  console.log('Analytics render, token:', token);
  const [loading, setLoading] = useState(true);
  const [salesByCountry, setSalesByCountry] = useState([]);
  const [salesByPayment, setSalesByPayment] = useState([]);
  const [orderStatusBreakdown, setOrderStatusBreakdown] = useState([]);
  const [paymentStatusBreakdown, setPaymentStatusBreakdown] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [error, setError] = useState(null);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    try {
      console.log('Fetching analytics with token:', token);
      const [countryRes, paymentRes, orderStatusRes, paymentStatusRes, bestSellersRes] = await Promise.all([
        fetch(`${baseUrl}/api/admin/analytics/sales-by-country`, { headers }),
        fetch(`${baseUrl}/api/admin/analytics/sales-by-payment-method`, { headers }),
        fetch(`${baseUrl}/api/admin/analytics/order-status-breakdown`, { headers }),
        fetch(`${baseUrl}/api/admin/analytics/payment-status-breakdown`, { headers }),
        fetch(`${baseUrl}/api/admin/analytics/best-selling-products`, { headers }),
      ]);
      console.log('Responses:', countryRes, paymentRes, orderStatusRes, paymentStatusRes, bestSellersRes);
      if (!countryRes.ok || !paymentRes.ok || !orderStatusRes.ok || !paymentStatusRes.ok || !bestSellersRes.ok) {
        throw new Error('Failed to fetch analytics data');
      }
      const [countryData, paymentData, orderStatusData, paymentStatusData, bestSellersData] = await Promise.all([
        countryRes.json(),
        paymentRes.json(),
        orderStatusRes.json(),
        paymentStatusRes.json(),
        bestSellersRes.json(),
      ]);
      setSalesByCountry(Object.entries(countryData).map(([country, count]) => ({ country, count })));
      setSalesByPayment(Object.entries(paymentData).map(([method, count]) => ({ method, count })));
      setOrderStatusBreakdown(ORDER_STATUSES.map((status, i) => ({
        status,
        count: orderStatusData[status] || 0,
        color: COLORS[i % COLORS.length],
      })));
      setPaymentStatusBreakdown(PAYMENT_STATUSES.map((status, i) => ({
        status,
        count: paymentStatusData[status] || 0,
        color: COLORS[i % COLORS.length],
      })));
      setBestSellers(Object.entries(bestSellersData)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name, qty]) => ({ name, qty })));
    } catch (err) {
      setError(err.message || 'Error fetching analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('useEffect running, token:', token);
    if (token) fetchAnalytics();
  }, [token]);

  if (loading) {
    return <div className="p-6 text-center text-lg">
      Loading analytics...
    </div>;
  }
  if (error) {
    return <div className="p-6 text-center text-red-500">{error}</div>;
  }

  // Responsive custom tooltip for Recharts
  const ResponsiveTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Use smaller font on mobile
      const isMobile = window.innerWidth < 640;
      const style = {
        fontSize: isMobile ? '12px' : '16px',
        maxWidth: isMobile ? 120 : 220,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        padding: 8,
        background: '#fff',
        border: '1px solid #eee',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      };
      return (
        <div style={style}>
          <div style={{ fontWeight: 600 }}>{label}</div>
          {payload.map((entry, i) => (
            <div key={i} style={{ color: entry.color, fontSize: isMobile ? '11px' : '14px' }}>
              {entry.dataKey === 'qty' ? (
                <>
                  <span style={{ color: '#00C49F' }}>qty : {entry.value}</span>
                </>
              ) : (
                <span>{entry.name} : {entry.value}</span>
              )}
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-2 md:p-6 max-w-6xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Analytics</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8">
        {/* Sales by Country - Horizontal Bar Chart */}
        <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-4 md:mb-0">
          <h3 className="text-lg font-semibold mb-4">Sales by Country</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={salesByCountry} layout="vertical" margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <XAxis type="number" allowDecimals={false} fontSize={window.innerWidth < 640 ? 10 : 12} tick={{ fontSize: window.innerWidth < 640 ? 10 : 12 }} />
              <YAxis dataKey="country" type="category" fontSize={window.innerWidth < 640 ? 10 : 12} width={window.innerWidth < 640 ? 60 : 90} tick={{ fontSize: window.innerWidth < 640 ? 10 : 12 }} />
              <Tooltip content={<ResponsiveTooltip />} />
              <Bar dataKey="count" fill="#0088FE">
                {salesByCountry.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {/* Sales by Payment Method - Donut Chart */}
        <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-4 md:mb-0">
          <h3 className="text-lg font-semibold mb-4">Sales by Payment Method</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={salesByPayment}
                dataKey="count"
                nameKey="method"
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={70}
                label
              >
                {salesByPayment.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
        {/* Order Status Breakdown - Horizontal Bar Chart */}
        <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-4 md:mb-0">
          <h3 className="text-lg font-semibold mb-4">Order Status Breakdown</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={orderStatusBreakdown} layout="vertical" margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <XAxis type="number" allowDecimals={false} fontSize={window.innerWidth < 640 ? 10 : 12} tick={{ fontSize: window.innerWidth < 640 ? 10 : 12 }} />
              <YAxis dataKey="status" type="category" fontSize={window.innerWidth < 640 ? 10 : 12} width={window.innerWidth < 640 ? 60 : 90} tick={{ fontSize: window.innerWidth < 640 ? 10 : 12 }} />
              <Tooltip content={<ResponsiveTooltip />} />
              <Bar dataKey="count" fill="#FF8042">
                {orderStatusBreakdown.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {/* Payment Status Breakdown - Area Chart */}
        <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-4 md:mb-0">
          <h3 className="text-lg font-semibold mb-4">Payment Status Breakdown</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={paymentStatusBreakdown} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="status" fontSize={window.innerWidth < 640 ? 10 : 12} tick={{ fontSize: window.innerWidth < 640 ? 10 : 12 }} />
              <YAxis allowDecimals={false} fontSize={window.innerWidth < 640 ? 10 : 12} tick={{ fontSize: window.innerWidth < 640 ? 10 : 12 }} />
              <Tooltip content={<ResponsiveTooltip />} />
              <Area type="monotone" dataKey="count" stroke="#00C49F" fill="#A28CF6" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {/* Best-Selling Products - Vertical Bar Chart */}
        <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-4 md:mb-0">
          <h3 className="text-lg font-semibold mb-4">Best-Selling Products</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={bestSellers} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
              <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} height={60} tick={{ fontSize: window.innerWidth < 900 ? 10 : 12 }} />
              <YAxis allowDecimals={false} fontSize={window.innerWidth < 640 ? 10 : 12} tick={{ fontSize: window.innerWidth < 640 ? 10 : 12 }} />
              <Tooltip content={<ResponsiveTooltip />} />
              <Bar dataKey="qty" fill="#00C49F">
                {bestSellers.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Analytics; 